import { getDecryptShareInfo, getEncryptShareInfo } from '@shared/service/user';
import logger from '@shared/utils/error/logger';

/** 参数加密工具类 */
export class ParamsEncryption {
	private static readonly CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

	private static stringToNumbers(str: string): number[] {
		return str.split('').map((char) => char.charCodeAt(0));
	}

	private static numbersToString(numbers: number[]): string {
		return String.fromCharCode(...numbers);
	}

	private static encodeString(str: string): string {
		const numbers = this.stringToNumbers(str);
		let num = 0n;
		for (let i = 0; i < numbers.length; i++) {
			num = num * 256n + BigInt(numbers[i]);
		}

		let result = '';
		while (num > 0n) {
			result = this.CHARS[Number(num % 62n)] + result;
			num = num / 62n;
		}
		return result || '0';
	}

	private static decodeString(encoded: string): string {
		let num = 0n;
		for (let i = 0; i < encoded.length; i++) {
			num = num * 62n + BigInt(this.CHARS.indexOf(encoded[i]));
		}

		const numbers: number[] = [];
		while (num > 0n) {
			numbers.unshift(Number(num % 256n));
			num = num / 256n;
		}

		return this.numbersToString(numbers);
	}

	// 创建缓存 Map
	private static readonly encryptionCache = new Map<string, string>();

	// 创建解密缓存 Map
	private static readonly decryptionCache = new Map<string, string>();

	static async encrypt(params: Record<string, string>) {
		try {
			// 生成缓存 key
			const cacheKey = JSON.stringify(params);

			// 先从缓存中查找
			if (this.encryptionCache.has(cacheKey)) {
				return this.encryptionCache.get(cacheKey) || '';
			}

			// 缓存未命中，调用接口
			const originCode = JSON.stringify(params);
			const { data } = await getEncryptShareInfo(originCode);

			// 保存到缓存
			this.encryptionCache.set(cacheKey, data);

			return data;
		} catch (error) {
			logger.error('用户分享', '加密接口错误:', error);
			try {
				const compressed = [
					params.invite_code || '-',
					params.app_id || '-',
					params.image_id || '-',
					params.post_id || '-',
					params.user_id || '-',
					params.type || '-',
					params.location || '-',
					params.topic_id || '-',
					params.platform || '-',
				]
					.map((s) => s.replace(/[^a-zA-Z0-9]/g, ''))
					.join(',');

				return this.encodeString(compressed);
			} catch (error) {
				logger.error('用户分享', '加密错误:', error);
				console.error('加密错误:', error);
				return '';
			}
		}
	}

	static async decrypt(token: string) {
		if (token.length <= 10) {
			// 先从缓存中查找
			if (this.decryptionCache.has(token)) {
				return JSON.parse(this.decryptionCache.get(token) || '{}');
			}

			try {
				const { data } = await getDecryptShareInfo(token);
				// 保存到缓存
				this.decryptionCache.set(token, data);
				const decryptedData = JSON.parse(data);
				return decryptedData;
			} catch (error) {
				logger.error('用户分享', '解密接口错误:', error);
				console.error('解密接口错误:', error);
			}
		}
		try {
			const decoded = this.decodeString(token);

			const [invite_code, app_id, image_id, post_id, user_id, type, location, topic_id, platform] =
				decoded.split(',').map((s) => (s === '' ? '-' : s));

			return {
				invite_code: invite_code === '-' ? '' : invite_code,
				app_id: app_id === '-' ? '' : app_id,
				image_id: image_id === '-' ? '' : image_id,
				post_id: post_id === '-' ? '' : post_id,
				user_id: user_id === '-' ? '' : user_id,
				type: type === '-' ? '' : type,
				location: location === '-' ? '' : location,
				topic_id: topic_id === '-' ? '' : topic_id,
				platform: platform === '-' ? '' : platform,
			};
		} catch (error) {
			logger.error('用户分享', '解密错误:', error);
			console.error('解密错误:', error);
			return {};
		}
	}
}
